// lib/footfall/volumes.ts
//
// The centre in 3D. Heights come from IGN's BD TOPO building register (measured heights and ground altitudes), not from
// the venue map, whose 3D scene is stylised (every shop is the same 1.5-unit slab). A mall's floor-to-floor height is
// the gap between the ground altitudes of the buildings holding each level's shops: IGN's storey counts include the
// housing and offices built above the shops, so they would make a shop look like a flat.
import type { PropertyVenue, PulseBuilding, PulseLayer } from '#types/types';
import { maxReviews, placeColour, ringCentre, type PulsePlace } from './pulse';
import { pointInRing } from './venue';

type Ring = Array<[number, number]>;
export type IgnBuilding = PulseBuilding;
export interface ShopVolume { base: number; top: number }
export interface CentreVolumes {
  /** Floor-to-floor height of the mall, in metres. */
  storeyHeight: number;
  /** 'ign' when measured from the register, 'default' when the register could not give it. */
  source: 'ign' | 'default';
  shops: Record<string, ShopVolume>;
  /** Buildings around the centre that hold none of its shops, at their measured height. */
  context: Array<{ id: string; rings: Ring[]; height: number }>;
}

export const IGN_SOURCE = 'IGN BD TOPO';
export const DEFAULT_STOREY = 5.5;
const STOREY_MIN = 4, STOREY_MAX = 8;
/** The gap left between a shop's ceiling and the floor above, so stacked levels read as separate. */
const SLAB = 0.6;
/** Context reaches this far around the shops, in degrees (about 150 m). */
const MARGIN = 0.0015;

/** The IGN request for the buildings around a centre: a public register that answers browsers directly. */
export function ignBuildingsUrl([[west, south], [east, north]]: [[number, number], [number, number]]): string {
  const box = [south - MARGIN, west - MARGIN, north + MARGIN, east + MARGIN].map(n => n.toFixed(6)).join(',');
  return `https://data.geopf.fr/wfs/ows?SERVICE=WFS&VERSION=2.0.0&REQUEST=GetFeature&TYPENAMES=BDTOPO_V3:batiment&BBOX=${box},urn:ogc:def:crs:EPSG::4326&OUTPUTFORMAT=application/json&COUNT=2000`;
}

const finite = (v: unknown) => typeof v === 'number' && Number.isFinite(v) ? v : null;
/** Reads IGN's GeoJSON answer, keeping only well-formed footprints. Rings are [lng, lat]; IGN's third coordinate is dropped. */
export function parseIgnBuildings(value: unknown): IgnBuilding[] {
  const features = value && typeof value === 'object' && Array.isArray((value as { features?: unknown }).features) ? (value as { features: unknown[] }).features : [];
  return features.flatMap((f, i) => {
    const feature = f as { id?: unknown; geometry?: { type?: string; coordinates?: unknown }; properties?: Record<string, unknown> };
    const polygons = feature.geometry?.type === 'MultiPolygon' ? feature.geometry.coordinates : feature.geometry?.type === 'Polygon' ? [feature.geometry.coordinates] : null;
    if (!Array.isArray(polygons)) return [];
    const rings = polygons.flatMap(p => Array.isArray(p) && Array.isArray(p[0]) ? [(p[0] as unknown[]).flatMap(c => Array.isArray(c) && finite(c[0]) !== null && finite(c[1]) !== null ? [[c[0], c[1]] as [number, number]] : [])] : []).filter(r => r.length >= 3);
    if (!rings.length) return [];
    const p = feature.properties ?? {};
    return [{ id: typeof p.cleabs === 'string' ? p.cleabs : String(feature.id ?? i), rings, height: finite(p.hauteur), groundAltitude: finite(p.altitude_minimale_sol), storeys: finite(p.nombre_d_etages), usage: typeof p.usage_1 === 'string' ? p.usage_1 : null }];
  });
}

const median = (values: number[]) => { const s = [...values].sort((a, b) => a - b); return s.length ? (s[Math.floor((s.length - 1) / 2)]! + s[Math.ceil((s.length - 1) / 2)]!) / 2 : null; };

/**
 * Every shop's volume and the context around the centre. Level 0's floor is height 0; a level above stands one storey
 * higher per level; a level below ground is drawn on its own from 0, since the map has no underground.
 */
export function centreVolumes(venue: PropertyVenue, buildings: IgnBuilding[]): CentreVolumes {
  const ordinal = new Map(venue.levels.map(l => [l.id, l.ordinal]));
  const units = venue.features.filter(f => f.kind === 'unit');
  const home = new Map<string, IgnBuilding>();
  for (const u of units) {
    const centre = ringCentre(u.rings);
    const b = buildings.find(b => b.rings.some(r => pointInRing(centre, r)));
    if (b) home.set(u.id, b);
  }
  // A level's floor altitude: the ground of the buildings holding only that level's shops, so a building spanning two
  // levels does not blur either.
  const levelsOf = new Map<string, Set<number>>();
  for (const u of units) { const b = home.get(u.id); if (b) levelsOf.set(b.id, (levelsOf.get(b.id) ?? new Set()).add(ordinal.get(u.level ?? '') ?? 0)); }
  const floorAltitude = (ord: number) => median(buildings.flatMap(b => b.groundAltitude !== null && levelsOf.get(b.id)?.size === 1 && levelsOf.get(b.id)!.has(ord) ? [b.groundAltitude] : []));
  const [ground, first] = [floorAltitude(0), floorAltitude(1)];
  const measured = ground !== null && first !== null ? first - ground : null;
  const ign = measured !== null && measured >= STOREY_MIN && measured <= STOREY_MAX;
  const storeyHeight = ign ? Math.round(measured! * 10) / 10 : DEFAULT_STOREY;
  const shops: Record<string, ShopVolume> = {};
  for (const u of units) {
    const ord = ordinal.get(u.level ?? '') ?? 0;
    const base = Math.round(Math.max(0, ord) * storeyHeight * 10) / 10;
    shops[u.id] = { base, top: Math.round((base + storeyHeight - SLAB) * 10) / 10 };
  }
  // Context is the neighbourhood only: a building whose footprint touches the centre's (its shops, services or
  // footprint) belongs to the centre, and drawn at full height it would bury the shops.
  const centre = venue.features.flatMap(f => f.rings);
  const touches = (b: IgnBuilding) => b.rings.some(r => r.some(p => centre.some(c => pointInRing(p, c)))) || centre.some(c => c.some(p => b.rings.some(r => pointInRing(p, r))));
  const context = buildings.filter(b => !levelsOf.has(b.id) && (b.height ?? 0) > 0 && !touches(b)).map(b => ({ id: b.id, rings: b.rings, height: b.height! }));
  return { storeyHeight, source: ign ? 'ign' : 'default', shops, context };
}

/**
 * The shops drawn in 3D for one level: that level's shops as coloured boxes ('active'), and the levels beneath it down
 * to ground as grey blocks ('below'), so the level in view stands on what holds it up. Levels above are left out, so
 * the level in view is never hidden under its own roof.
 */
export function levelVolumes(places: PulsePlace[], volumes: CentreVolumes, levels: PropertyVenue['levels'], levelId: string | null, layer: PulseLayer, colours?: Record<string, string>) {
  const ordinal = new Map(levels.map(l => [l.id, l.ordinal]));
  const active = ordinal.get(levelId ?? '') ?? 0;
  const most = maxReviews(places);
  const features = places.flatMap((place, i) => {
    const ord = ordinal.get(place.feature.level ?? '') ?? 0;
    const role = place.feature.level === levelId ? 'active' : active > 0 && ord >= 0 && ord < active ? 'below' : null;
    const volume = volumes.shops[place.feature.id];
    if (!role || !volume) return [];
    return [{ type: 'Feature' as const, id: i, properties: { id: place.feature.id, role, colour: colours?.[place.feature.id] ?? placeColour(place, layer, most), base: volume.base, top: volume.top },
      geometry: { type: 'MultiPolygon' as const, coordinates: place.feature.rings.map(ring => [[...ring, ring[0]!]]) } }];
  });
  return { type: 'FeatureCollection' as const, features };
}

export const contextVolumes = (volumes: CentreVolumes) => ({ type: 'FeatureCollection' as const, features: volumes.context.map((b, i) => ({
  type: 'Feature' as const, id: i, properties: { id: b.id, height: b.height }, geometry: { type: 'MultiPolygon' as const, coordinates: b.rings.map(ring => [[...ring, ring[0]!]]) } })) });
